import { Component, DestroyRef, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { CanComponentDeactivate, warnOnBrowserUnloadIfUnsaved } from 'src/app/core/guards/unsaved-changes.guard';
import { UploadProgressService } from 'src/app/core/services/upload-progress.service';
import {
  ApiException,
  DocumentUploadJobState,
  DocumentUploadPreviewViewModel,
  EsraPipingAPIClient,
  FileParameter,
  FileResponse,
  ShareLinkClass,
} from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';
import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  describeAmbiguousFileName,
  filterAcceptedDocuments,
  INDEX_FILE_EXTENSION,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  normalizeFileName,
  splitAmbiguousFileNames,
} from './bu-pdf-bulk-upload.constants';

/**
 * Session storage key holding the open upload job. The job lives on the server (database and shared storage), so a refresh, or
 * leaving and coming back, resumes the review instead of losing it. The reference is only removed when the job is finished.
 */
const ACTIVE_JOB_STORAGE_KEY: string = 'esra.buPdfBulkUpload.activeJobId';

@Component({
  selector: 'app-bu-pdf-bulk-upload',
  templateUrl: './bu-pdf-bulk-upload.component.html',
  styleUrls: ['./bu-pdf-bulk-upload.component.scss'],
  standalone: false,
})
export class BuPdfBulkUploadComponent implements OnInit, CanComponentDeactivate {
  private readonly esraPipingApiClient: EsraPipingAPIClient = inject(EsraPipingAPIClient);
  private readonly snackBarService: SnackBarService = inject(SnackBarService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly uploadProgressService: UploadProgressService = inject(UploadProgressService);
  /** Exposed so the template can compare against `ShareLinkClass.PdfPipeClass`/`PdfValve`. */
  readonly ShareLinkClass = ShareLinkClass;

  // ── Document type (mutually exclusive -- an upload is either all pipe classes or all valves) ──
  documentType: ShareLinkClass = ShareLinkClass.PdfPipeClass;

  // ── File selection state ──────────────────────────────────────────────────
  indexFile: File | null = null;
  documents: File[] = [];
  isDraggingIndex: boolean = false;
  isDraggingDocuments: boolean = false;

  // ── Preview state ──────────────────────────────────────────────────────────
  /** The open upload job, shown for review in place of the selection form. */
  readonly preview: WritableSignal<DocumentUploadPreviewViewModel | null> = signal<DocumentUploadPreviewViewModel | null>(null);
  /** The documents selected next to the index file; the preview sends them right after it opens. Empty when a job was resumed. */
  readonly documentsToSend: WritableSignal<File[]> = signal<File[]>([]);
  /** True while the job holds work that is not confirmed yet; leaving the page then discards it. */
  isJobActive: boolean = false;

  // ── Request state ──────────────────────────────────────────────────────────
  isUploading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isRestoring$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** Upload percentage (0-100); only reaches 100 once the server has responded. */
  readonly uploadProgress: Signal<number> = this.uploadProgressService.progress;
  /** Inline banner error -- mirrors the backend's plain-text 400 validation message. */
  uploadErrorMessage: string | null = null;

  // ── Template download state ────────────────────────────────────────────────
  isDownloadingPipeClassTemplate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isDownloadingValveTemplate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Exposed for the template's `accept` attribute and hint text. */
  readonly allowedDocumentExtensions: ReadonlyArray<string> = ALLOWED_DOCUMENT_EXTENSIONS;
  readonly maxFileSizeMb: number = MAX_FILE_SIZE_MB;

  constructor() {
    warnOnBrowserUnloadIfUnsaved(() => this.hasUnsavedChanges());
  }

  get documentTypeLabel(): string {
    return this.documentType === ShareLinkClass.PdfPipeClass ? 'pipe class' : 'valve';
  }

  get isPreviewDisabled(): boolean {
    return !this.indexFile || this.isUploading$.getValue();
  }

  ngOnInit(): void {
    this.resumeActiveJob();
  }

  /** `CanComponentDeactivate`: leaving while a preview is open only warns; the job is kept and can be resumed. */
  hasUnsavedChanges(): boolean {
    return this.isJobActive;
  }

  // ── Document type selection ───────────────────────────────────────────────

  /** Switching type clears any in-progress selection so files are never mixed across types. */
  onDocumentTypeChange(type: ShareLinkClass): void {
    this.documentType = type;
    this.indexFile = null;
    this.documents = [];
    this.uploadErrorMessage = null;
  }

  // ── Index file selection ──────────────────────────────────────────────────

  onIndexFileSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    this.setIndexFile(input.files?.[0] ?? null);
    input.value = '';
  }

  onIndexFileDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingIndex = false;
    this.setIndexFile(event.dataTransfer?.files?.[0] ?? null);
  }

  onIndexDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingIndex = true;
  }

  onIndexDragLeave(): void {
    this.isDraggingIndex = false;
  }

  clearIndexFile(): void {
    this.indexFile = null;
  }

  private setIndexFile(file: File | null): void {
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith(INDEX_FILE_EXTENSION)) {
      this.snackBarService.showError('The index file must be an Excel workbook (.xlsx).');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.snackBarService.showError(`The index file exceeds the maximum size of ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }
    this.indexFile = file;
  }

  // ── Document selection ─────────────────────────────────────────────────────

  onDocumentsSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    this.addDocuments(input.files);
    input.value = '';
  }

  onDocumentsDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingDocuments = false;
    this.addDocuments(event.dataTransfer?.files ?? null);
  }

  onDocumentsDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingDocuments = true;
  }

  onDocumentsDragLeave(): void {
    this.isDraggingDocuments = false;
  }

  removeDocument(file: File): void {
    this.documents = this.documents.filter((document: File) => document !== file);
  }

  clearDocuments(): void {
    this.documents = [];
  }

  private addDocuments(fileList: FileList | null): void {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const existingNames: Set<string> = new Set<string>(this.documents.map((document: File) => normalizeFileName(document.name)));
    const { accepted, unsupportedNames, oversizedNames } = filterAcceptedDocuments(Array.from(fileList));
    const { unique, ambiguousNames } = splitAmbiguousFileNames(accepted);
    const newDocuments: File[] = [...this.documents];

    // A name that more than one file carries (in this selection or with a file already listed) is ambiguous, so none of those is added.
    for (const file of unique) {
      if (existingNames.has(normalizeFileName(file.name))) {
        ambiguousNames.push(file.name);
      } else {
        newDocuments.push(file);
        existingNames.add(normalizeFileName(file.name));
      }
    }

    this.documents = newDocuments;

    if (ambiguousNames.length > 0) {
      this.snackBarService.showError(ambiguousNames.map((name: string) => describeAmbiguousFileName(name)).join(' '));
    }

    if (unsupportedNames.length > 0) {
      this.snackBarService.showError(
        `Unsupported file type. Accepted: ${this.allowedDocumentExtensions.join(', ')}. Skipped: ${unsupportedNames.join(', ')}`
      );
    }

    if (oversizedNames.length > 0) {
      this.snackBarService.showError(
        `Files must be ${MAX_FILE_SIZE_MB} MB or smaller. Skipped: ${oversizedNames.join(', ')}`
      );
    }
  }

  // ── Preview ─────────────────────────────────────────────────────────────────

  /**
   * Sends the index file alone and opens the editable preview as soon as it comes back, with every expected document missing.
   * The selected documents are then sent by the preview in bounded batches while the user reviews the rows (a second kind of request).
   * Nothing is created yet: the server stores the documents and the upload job durably, and the records only exist once the user confirms.
   */
  onPreview(): void {
    const indexFile: File | null = this.indexFile;
    if (this.isPreviewDisabled || !indexFile) {
      return;
    }

    this.uploadErrorMessage = null;
    this.uploadProgressService.reset();
    this.isUploading$.next(true);

    const indexFileParameter: FileParameter = { data: indexFile, fileName: indexFile.name };

    this.esraPipingApiClient.createDocumentUpload(this.documentType, indexFileParameter)
      .pipe(
        finalize(() => this.isUploading$.next(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (preview: DocumentUploadPreviewViewModel) => this.openPreview(preview, [...this.documents]),
        error: (err: unknown) => this.handleUploadError(err),
      });
  }

  /**
   * Called when the preview component closes: after the records were created (`wasCompleted`) or after the job was discarded.
   * @param wasCompleted True when the records were created, so the selection is no longer needed.
   */
  onPreviewClosed(wasCompleted: boolean): void {
    this.isJobActive = false;
    this.clearStoredJobId();
    this.preview.set(null);
    this.documentsToSend.set([]);
    if (wasCompleted) {
      this.indexFile = null;
      this.documents = [];
    }
  }

  onPreviewActiveChange(isActive: boolean): void {
    this.isJobActive = isActive;
  }

  private openPreview(preview: DocumentUploadPreviewViewModel, documentsToSend: File[] = []): void {
    this.isJobActive = preview.status?.state === DocumentUploadJobState.Preview;
    if (preview.jobId) {
      sessionStorage.setItem(ACTIVE_JOB_STORAGE_KEY, preview.jobId);
    }
    this.documentsToSend.set(documentsToSend);
    this.preview.set(preview);
  }

  /** After a refresh, reopens the job that was being reviewed. A job that no longer exists is forgotten silently. */
  private resumeActiveJob(): void {
    const storedJobId: string | null = sessionStorage.getItem(ACTIVE_JOB_STORAGE_KEY);
    if (!storedJobId) {
      return;
    }

    this.isRestoring$.next(true);
    this.esraPipingApiClient.getDocumentUpload(storedJobId)
      .pipe(
        finalize(() => this.isRestoring$.next(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (preview: DocumentUploadPreviewViewModel) => {
          if (preview.status?.state === DocumentUploadJobState.Preview || preview.status?.state === DocumentUploadJobState.Committing) {
            this.openPreview(preview);
          } else {
            this.clearStoredJobId();
          }
        },
        error: () => this.clearStoredJobId(),
      });
  }

  private clearStoredJobId(): void {
    sessionStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
  }

  /** The backend returns 400s as a plain-text body, carried on `ApiException.response`. */
  private handleUploadError(err: unknown): void {
    const rawError: unknown = err instanceof ApiException ? err.response : err;
    const message: string = getApiErrorMessage(rawError, 'The files could not be validated. Please check them and try again.');
    this.uploadErrorMessage = message;
    this.snackBarService.showError(message);
  }

  // ── Template downloads ────────────────────────────────────────────────────

  downloadPipeClassTemplate(): void {
    this.downloadTemplate(ShareLinkClass.PdfPipeClass, this.isDownloadingPipeClassTemplate$, 'PipeClassIndexTemplate.xlsx');
  }

  downloadValveTemplate(): void {
    this.downloadTemplate(ShareLinkClass.PdfValve, this.isDownloadingValveTemplate$, 'ValveIndexTemplate.xlsx');
  }

  private downloadTemplate(type: ShareLinkClass, indicator: BehaviorSubject<boolean>, defaultFileName: string): void {
    indicator.next(true);
    this.esraPipingApiClient.getDocumentIndexTemplate(type)
      .pipe(
        finalize(() => indicator.next(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response: FileResponse) => this.saveFile(response, defaultFileName),
        error: (err: unknown) => {
          this.snackBarService.showError(getApiErrorMessage(err, 'Failed to download the template. Please try again.'));
        },
      });
  }

  private saveFile(response: FileResponse, defaultFileName: string): void {
    const file: Blob = response.data;
    const fileUrl: string = URL.createObjectURL(file);
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = response.fileName || defaultFileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(fileUrl);
  }
}
